import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { BookmarkIcon } from '@solar-icons/react/linear/bookmark'
import { CheckCircleIcon } from '@solar-icons/react/linear/check-circle'
import { PenIcon } from '@solar-icons/react/linear/pen'
import { PlayIcon } from '@solar-icons/react/bold/play'
import { AddIcon } from '@solar-icons/react/linear/add'
import { TrashBinTrashIcon } from '@solar-icons/react/linear/trash-bin-trash'
import { CloseIcon } from '@solar-icons/react/linear/close'
import {
  Badge,
  Button,
  ConfirmButton,
  EmptyState,
  Field,
  Input,
  Panel,
} from '@/components/ui'
import { useSavedStore } from '@/stores/savedStore'
import type { SavedQuery } from '@/stores/savedStore'
import { formatRelativeTime } from '@/lib/format/cells'
import { useRunQuery } from '@/lib/hooks/useRunQuery'

function SavedRow({
  item,
  onRun,
}: {
  item: SavedQuery
  onRun: (query: string) => void
}) {
  const update = useSavedStore((state) => state.update)
  const remove = useSavedStore((state) => state.remove)

  const [editing, setEditing] = useState(false)
  const [label, setLabel] = useState(item.label)
  const [query, setQuery] = useState(item.query)

  const commit = () => {
    update(item.id, { label: label.trim() || item.label, query: query.trim() })
    setEditing(false)
  }

  const cancel = () => {
    setLabel(item.label)
    setQuery(item.query)
    setEditing(false)
  }

  if (editing) {
    return (
      <li className="rounded-xl border border-accent-border bg-surface p-4">
        <div className="space-y-3">
          <Field label="Label">
            <Input
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              placeholder="A short name"
            />
          </Field>
          <Field label="Query">
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="The question to ask"
            />
          </Field>
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              icon={<CheckCircleIcon size={14} strokeWidth={2} />}
              onClick={commit}
              disabled={!query.trim()}
            >
              Save
            </Button>
            <Button variant="ghost" size="sm" icon={<CloseIcon size={14} strokeWidth={2} />} onClick={cancel}>
              Cancel
            </Button>
          </div>
        </div>
      </li>
    )
  }

  return (
    <li className="group rounded-xl border border-line bg-surface px-4 py-3 transition-colors hover:border-line-strong">
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={() => onRun(item.query)}
          className="min-w-0 flex-1 text-left"
          title="Run this query"
        >
          <p className="truncate text-sm font-medium text-fg">{item.label}</p>
          <p className="mt-0.5 truncate font-mono text-[11px] text-muted">
            {item.query}
          </p>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {item.useCount > 0 && (
              <Badge>
                used {item.useCount} {item.useCount === 1 ? 'time' : 'times'}
              </Badge>
            )}
            <span className="text-[11px] text-subtle">
              added {formatRelativeTime(item.createdAt)}
            </span>
          </div>
        </button>

        <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
          <Button
            variant="ghost"
            size="sm"
            icon={<PlayIcon size={13} />}
            title="Run"
            aria-label="Run"
            onClick={() => onRun(item.query)}
          />
          <Button
            variant="ghost"
            size="sm"
            icon={<PenIcon size={13} strokeWidth={1.75} />}
            title="Edit"
            aria-label="Edit"
            onClick={() => setEditing(true)}
          />
          <Button
            variant="ghost"
            size="sm"
            icon={<TrashBinTrashIcon size={13} strokeWidth={1.75} />}
            title="Delete"
            aria-label="Delete"
            onClick={() => remove(item.id)}
          />
        </div>
      </div>
    </li>
  )
}

export function SavedView() {
  const navigate = useNavigate()

  const items = useSavedStore((state) => state.items)
  const save = useSavedStore((state) => state.save)
  const markUsed = useSavedStore((state) => state.markUsed)
  const clear = useSavedStore((state) => state.clear)
  const { run: runQuery } = useRunQuery()

  const [draft, setDraft] = useState('')

  const run = (query: string) => {
    const match = items.find((item) => item.query === query)
    if (match) markUsed(match.id)
    void runQuery(query)
    navigate('/')
  }

  const add = () => {
    if (!draft.trim()) return
    save({ query: draft })
    setDraft('')
  }

  return (
    <div className="scrollbar-thin h-full overflow-y-auto">
      <div className="mx-auto w-full max-w-4xl space-y-4 px-4 py-6">
        <Panel
          title="Add a query"
          description="Saved queries persist in this browser and appear in the chat sidebar."
        >
          <div className="flex items-end gap-2">
            <Input
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') add()
              }}
              placeholder="e.g. Show top 5 brokers by Net P&L"
              aria-label="New saved query"
            />
            <Button
              variant="primary"
              icon={<AddIcon size={15} strokeWidth={2} />}
              onClick={add}
              disabled={!draft.trim()}
            >
              Save
            </Button>
          </div>
        </Panel>

        {items.length === 0 ? (
          <EmptyState
            icon={<BookmarkIcon size={20} strokeWidth={1.75} />}
            title="Nothing saved yet"
            description="Save a query from the chat thread, from History, or with the box above."
          />
        ) : (
          <>
            <div className="flex items-center justify-between px-1">
              <span className="text-xs text-subtle">
                {items.length} saved {items.length === 1 ? 'query' : 'queries'}
              </span>
              <ConfirmButton
                onConfirm={clear}
                confirmLabel="Delete all saved?"
                icon={<TrashBinTrashIcon size={14} strokeWidth={1.75} />}
              >
                Clear all
              </ConfirmButton>
            </div>
            <ul className="space-y-2">
              {items.map((item) => (
                <SavedRow key={item.id} item={item} onRun={run} />
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  )
}
